import { useMemo } from 'react'
import { Alert, Card, Progress, Skeleton, Space, Typography } from 'antd'
import { TeamOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { reportWindow } from '@/shared/lib/dayRange'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatBytes } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { fetchTopConsumersReport } from '@/features/reports/reportsApi'

const WINDOW_DAYS = 7
const ROWS = 5

/**
 * Eng ko'p trafik sarflagan abonentlar — oxirgi 7 kun.
 *
 * <h2>Nega bosh sahifada</h2>
 * "Kanal to'lib ketdi" degan shikoyat kelganda birinchi savol — KIM
 * sarflayapti. Bu ma'lumot allaqachon bor edi (`GET /api/reports/top-consumers`),
 * lekin faqat Hisobotlar sahifasida, ya'ni operator uni ATAYLAB izlaganda
 * topardi.
 *
 * <h2>⚠️ Har qator BOSILADI</h2>
 * Login — abonent kartochkasiga havola. Aks holda operator ismni ko'rib,
 * uni qo'lda Abonentlar sahifasida qidirishi kerak bo'lardi.
 *
 * <h2>⚠️ Oyna 7 KUN, "bugun" EMAS</h2>
 * Bugungi son ertalab soat 9 da deyarli hamma uchun nolga yaqin bo'ladi va
 * reytingni tasodifiy qilardi. Bir haftalik oyna esa barqaror manzara
 * beradi.
 */
export function TopConsumersPanel() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const allowed = can('report.read')

  // ⚠️ Oyna TENANT kalendarida. Ilgari `dayjs()` — brauzer kuni — olinardi
  // va Kobul tenantida oyna bir kunga surilib ketardi (`dayRange.ts`).
  const range = useMemo(() => reportWindow(WINDOW_DAYS), [])

  const query = useQuery({
    queryKey: queryKeys.reports.topConsumers(range.from, range.to, ROWS),
    queryFn: () => fetchTopConsumersReport(range, ROWS),
    enabled: allowed,
  })

  if (!allowed) return null

  const rows = query.data ?? []
  const max = Math.max(1, ...rows.map((r) => r.totalBytes))

  return (
    <Card
      title={t('dashboard.topConsumers.title')}
      extra={<Link to="/reports">{t('dashboard.openReports')}</Link>}
      size="small"
      // Yonidagi grafik kartasi balandroq — bu karta cho'zilmasa qatorda
      // ochiq teshik qolardi
      style={{ height: '100%' }}
    >
      {query.isError ? (
        <Alert type="error" showIcon message={translateError(query.error, t)} />
      ) : query.isPending ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : rows.length === 0 ? (
        <EmptyState icon={<TeamOutlined />} title={t('dashboard.topConsumers.empty')} />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {rows.map((row) => (
            <div key={row.subscriberId}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                {/* ⚠️ Havola `subscriberId` bo'yicha, login bo'yicha EMAS:
                    login o'zgarishi mumkin, id o'zgarmaydi
                    (`reportsApi.topConsumerSchema` izohi) */}
                <Link to={`/subscribers/${row.subscriberId}`}>
                  <Typography.Text ellipsis style={{ maxWidth: 160 }}>
                    {row.login}
                  </Typography.Text>
                </Link>
                <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {formatBytes(row.totalBytes)}
                </Typography.Text>
              </div>
              <Progress
                percent={Math.round((row.totalBytes / max) * 100)}
                showInfo={false}
                size="small"
                style={{ margin: 0, lineHeight: 1 }}
              />
            </div>
          ))}
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('dashboard.topConsumers.windowHint', { days: WINDOW_DAYS })}
          </Typography.Text>
        </Space>
      )}
    </Card>
  )
}
